/**
 * Workstream: brief, tasks in dependency order (with what each waits on), proposed tasks with
 * bulk approve (and "always"), session history, builds, and the live session — side by side on
 * desktop, one tap away on mobile.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { AgentGlyph, Button, Chip, Sheet, StatusIcon, type StatusKind } from '@viko/transcript-ui'
import { ArrowUpRight, Check, ChevronLeft, GitBranch, Pencil, Play, Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useAppUi } from '../components/app-ui.js'
import { BuildView, FeedbackSheet } from '../components/HubCards.js'
import { statusOf } from '../components/RunRow.js'
import { SessionView } from '../components/SessionView.js'
import { Ago, Empty, errorText, SectionTitle, SkeletonRows, useToast } from '../components/ui.js'
import { useBackend } from '../lib/backend.js'
import { projectHref, runHref, taskHref } from '../lib/ia.js' // ia-shell
import './workstream.css'
import {
  qk,
  useBuilds,
  useMachines,
  useProjects,
  useRuns,
  useTask,
  useTasks,
  useWorkstream,
} from '../lib/queries.js'
import type { BuildCard, TaskDetail, WorkstreamDetail } from '../lib/types.js'
import { isOpen, openDeps, orderTasks, progressOf } from '../lib/work.js'

export function WorkstreamPage() {
  const { workstreamId } = useParams({ strict: false }) as { workstreamId: string }
  const ws = useWorkstream(workstreamId)
  const w = ws.data
  if (ws.isPending) return <SkeletonRows n={4} />
  if (!w) return <Empty title="Lane not found">{errorText(ws.error)}</Empty>
  // app-clarity lane page: one column (title, meta, live run, brief, queue, sessions). The live
  // run is a row that opens the run; the run view is never embedded a second time here.
  return (
    <div className="app-list flex-1">
      <WorkstreamDetailView w={w} />
    </div>
  )
}

const RUN_KIND = (status: string): StatusKind =>
  status === 'waiting'
    ? 'wait'
    : status === 'error'
      ? 'fail'
      : status === 'running' || status === 'compacting'
        ? 'run'
        : 'idle'

function WorkstreamDetailView({ w }: { w: WorkstreamDetail }) {
  const projects = useProjects()
  const machines = useMachines()
  const runs = useRuns()
  const ui = useAppUi()
  const project = projects.data?.find((p) => p.id === w.projectId)
  const machine = machines.data?.find((m) => m.id === (w.liveRun?.machineId ?? w.machineId))
  const tasks = useTasks({ workstreamId: w.id })
  const all = tasks.data ?? []
  const proposed = all.filter((t) => t.status === 'proposed')
  const ordered = orderTasks(all.filter((t) => t.status !== 'proposed' && t.status !== 'cancelled'))
  const prog = progressOf(all)
  const next = ordered.find((t) => t.status === 'ready' && !openDeps(t, all).length)
  const live = w.liveRun
  const liveRun = live ? runs.data?.find((r) => r.runId === live.runId) : undefined
  const state = live
    ? live.status === 'waiting'
      ? { word: 'needs you', kind: 'wait' as const }
      : { word: live.status === 'running' ? 'running' : live.status, kind: RUN_KIND(live.status) }
    : { word: w.paused ? 'paused' : w.status, kind: 'idle' as const }
  return (
    <div className="ln">
      <p className="ln__crumb">
        <Link
          to={projectHref(w.projectId)}
          className="ln__back"
          aria-label={`Back to ${project?.name ?? 'project'}`}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          <span>{project?.name ?? 'Project'} · Lanes</span>
        </Link>
      </p>
      <header className="ln__head">
        <h1 className="ln__title">{w.name}</h1>
        {!live ? (
          <Button
            variant="primary"
            size="sm"
            icon={<Play size={14} />}
            onClick={() =>
              ui.openStart({
                projectId: w.projectId,
                workstreamId: w.id,
                workstreamName: w.name,
                ...(next ? { taskId: next.id, prompt: next.title } : { prompt: w.brief ?? w.name }),
                ...(w.machineId ? { machineId: w.machineId } : {}),
              })
            }
          >
            Start session
          </Button>
        ) : null}
      </header>
      <p className="ln__meta">
        <span className={`ln__state ln__state--${state.kind}`}>
          <StatusIcon kind={state.kind} size={16} live={state.kind === 'run'} />
          <span>{state.word}</span>
        </span>
        {w.branch ? (
          <span className="ln__fact">
            <GitBranch size={14} aria-hidden="true" />
            <code>{w.branch}</code>
          </span>
        ) : null}
        {machine ? <span className="ln__fact">on {machine.name}</span> : null}
        {prog.total ? (
          <span className="ln__fact">
            {prog.done} of {prog.total} tasks done
          </span>
        ) : null}
        {w.bulkApproved ? <span className="ln__fact">follow-ups approve themselves</span> : null}
      </p>
      {live ? (
        <Link
          to={runHref(live.runId, w.projectId)}
          className="ln__row ln__live"
          aria-label={`Open the live run: ${liveRun?.title ?? 'session'}`}
        >
          <span className="ln__icon">
            <StatusIcon kind={RUN_KIND(live.status)} size={16} />
          </span>
          <span className="ln__text">
            <span className="ln__rowtitle">{liveRun?.title ?? 'Live session'}</span>
            <span className="ln__rowmeta">
              {live.status === 'waiting' ? (
                <span className="text-status-wait">waiting for you</span>
              ) : (
                <span>{statusOf(liveRun ?? ({ status: live.status } as never)).label}</span>
              )}
              {' · '}
              {live.agent === 'codex' ? 'Codex' : 'Claude'}
              {machine ? ` on ${machine.name}` : ''}
              {liveRun?.startedAt ? (
                <>
                  {' · '}
                  <Ago ts={liveRun.startedAt} />
                </>
              ) : null}
            </span>
          </span>
          <span className="ln__go">
            Open run <ArrowUpRight size={14} aria-hidden="true" />
          </span>
        </Link>
      ) : null}
      <Brief w={w} />
      {proposed.length ? <Proposed w={w} tasks={proposed} /> : null}
      <Tasks w={w} ordered={ordered} all={all} loading={tasks.isPending} />
      <History workstreamId={w.id} projectId={w.projectId} />
      <Builds w={w} tasks={all} />
    </div>
  )
}

/** A section head: 15 px semibold title, a muted count, actions at the right. */
function LaneSection({
  id,
  title,
  count,
  action,
  children,
}: {
  id: string
  title: string
  count?: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section aria-labelledby={id} className="ln__sec">
      <div className="ln__sechead">
        <h2 id={id}>{title}</h2>
        {count != null ? <span className="ln__count">{count}</span> : null}
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  )
}

function Brief({ w }: { w: WorkstreamDetail }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(w.brief ?? '')
  const save = async () => {
    try {
      await b.api.updateWorkstream(w.id, { brief: text })
      await qc.invalidateQueries({ queryKey: qk.workstream(w.id) })
      setEditing(false)
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <LaneSection
      id="ws-brief"
      title="Brief"
      action={
        editing ? null : (
          <Button
            size="sm"
            variant="ghost"
            icon={<Pencil size={13} />}
            onClick={() => {
              setText(w.brief ?? '')
              setEditing(true)
            }}
          >
            Edit
          </Button>
        )
      }
    >
      {editing ? (
        <div className="grid gap-2">
          <textarea
            className="input"
            rows={6}
            aria-label="Brief"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={() => void save()}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="ln__prose">
          {w.brief || 'No brief yet. Agents get the brief, the task and the project rules.'}
        </p>
      )}
    </LaneSection>
  )
}

function Proposed({ w, tasks }: { w: WorkstreamDetail; tasks: TaskDetail[] }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const approve = async (opts: { standing?: boolean; taskIds?: string[] }) => {
    try {
      const r = await b.api.approveProposed(w.id, opts)
      toast(
        `Approved ${r.approved.length} task${r.approved.length === 1 ? '' : 's'}${r.standing ? '; future follow-ups too' : ''}`,
      )
      await qc.invalidateQueries({ queryKey: ['tasks'] })
      await qc.invalidateQueries({ queryKey: qk.workstream(w.id) })
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <LaneSection
      id="ws-proposed"
      title={`Proposed follow-up${tasks.length === 1 ? '' : 's'}`}
      count={tasks.length}
      action={
        <span className="ln__actions">
          <Button size="sm" variant="ghost" onClick={() => void approve({ standing: true })}>
            Approve all + future
          </Button>
          <Button size="sm" icon={<Check size={14} />} onClick={() => void approve({})}>
            Approve all
          </Button>
        </span>
      }
    >
      <ul className="ln__list">
        {tasks.map((t) => (
          <li key={t.id} className="ln__row">
            <span className="ln__icon">
              <StatusIcon kind="idle" size={16} label="Proposed" />
            </span>
            <span className="ln__text">
              <Link to={taskHref(t.id, w.projectId)} className="ln__rowtitle ln__link">
                {t.title}
              </Link>
              <span className="ln__rowmeta">
                {t.rationale ? `${t.rationale} · ` : ''}proposed by {t.provenance}
              </span>
            </span>
            <Button size="sm" variant="ghost" onClick={() => void approve({ taskIds: [t.id] })}>
              Approve
            </Button>
          </li>
        ))}
      </ul>
    </LaneSection>
  )
}

const TASK_KIND = (s: string): StatusKind =>
  s === 'done'
    ? 'ok'
    : s === 'running' || s === 'landing' || s === 'review'
      ? 'run'
      : s === 'blocked'
        ? 'blocked'
        : 'idle'

const TASK_ICON = (s: string) => (
  <StatusIcon kind={TASK_KIND(s)} size={16} label={s === 'ready' ? 'Ready' : s} live={false} />
)

function Tasks({
  w,
  ordered,
  all,
  loading,
}: {
  w: WorkstreamDetail
  ordered: TaskDetail[]
  all: TaskDetail[]
  loading: boolean
}) {
  const [adding, setAdding] = useState(false)
  return (
    <LaneSection
      id="ws-tasks"
      title="Tasks"
      count={`${ordered.filter((t) => isOpen(t.status)).length} open`}
      action={
        <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setAdding(true)}>
          Task
        </Button>
      }
    >
      {loading ? <SkeletonRows n={3} /> : null}
      {!loading && !ordered.length ? <p className="ln__empty">No tasks yet.</p> : null}
      <ol className="ln__list">
        {ordered.map((t) => {
          const waits = openDeps(t, all)
          return (
            <li key={t.id}>
              <Link to={taskHref(t.id, w.projectId)} className="ln__row ln__row--link">
                <span className="ln__icon">{TASK_ICON(t.status)}</span>
                <span className="ln__text">
                  <span className="ln__rowtitle">{t.title}</span>
                  <span className="ln__rowmeta">
                    {t.status}
                    {t.progress != null && t.status === 'running' ? ` · ${t.progress}%` : ''}
                    {t.blockedReason ? ` · ${t.blockedReason}` : ''}
                    {waits.length ? ` · waits on ${waits.map((d) => d.title).join(', ')}` : ''}
                    {t.labels.length ? ` · ${t.labels.slice(0, 2).join(', ')}` : ''}
                  </span>
                </span>
              </Link>
            </li>
          )
        })}
      </ol>
      {adding ? <NewTask w={w} all={all} onClose={() => setAdding(false)} /> : null}
    </LaneSection>
  )
}

function NewTask({
  w,
  all,
  onClose,
}: {
  w: WorkstreamDetail
  all: TaskDetail[]
  onClose: () => void
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [deps, setDeps] = useState<string[]>([])
  const create = async () => {
    try {
      await b.api.createTask({
        workstreamId: w.id,
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(deps.length ? { deps } : {}),
      })
      await qc.invalidateQueries({ queryKey: ['tasks'] })
      onClose()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const candidates = all.filter((t) => t.status !== 'done' && t.status !== 'cancelled')
  return (
    <Sheet
      open
      onClose={onClose}
      title="New task"
      subtitle={w.name}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!title.trim()} onClick={() => void create()}>
            Add task
          </Button>
        </>
      }
    >
      <div className="grid gap-3 pb-2">
        <label className="field">
          <span>Title</span>
          <input
            className="input"
            value={title}
            // biome-ignore lint/a11y/noAutofocus: primary input of the sheet
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Description (optional)</span>
          <textarea
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        {candidates.length ? (
          <fieldset className="field m-0 border-0 p-0">
            <legend className="pb-1 text-[14px] font-medium text-text-2">Depends on</legend>
            {candidates.map((t) => (
              <label key={t.id} className="flex items-center gap-2 py-1 text-[15px]">
                <input
                  type="checkbox"
                  checked={deps.includes(t.id)}
                  onChange={(e) =>
                    setDeps((d) => (e.target.checked ? [...d, t.id] : d.filter((x) => x !== t.id)))
                  }
                />
                {t.title}
              </label>
            ))}
          </fieldset>
        ) : null}
      </div>
    </Sheet>
  )
}

function History({ workstreamId, projectId }: { workstreamId: string; projectId: string }) {
  const runs = useRuns()
  const list = (runs.data ?? []).filter((r) => r.workstreamId === workstreamId)
  if (!list.length) return null
  return (
    <LaneSection id="ws-history" title="Sessions" count={list.length}>
      <ul className="ln__list">
        {list.map((r) => {
          const s = statusOf(r)
          return (
            <li key={r.runId}>
              <Link to={runHref(r.runId, projectId)} className="ln__row ln__row--link">
                <span className="ln__icon">
                  <AgentGlyph agent={r.agent ?? 'claude'} size={16} />
                </span>
                <span className="ln__text">
                  <span className="ln__rowtitle">{r.title ?? r.runId}</span>
                  <span className="ln__rowmeta">
                    <span className={s.tone}>{s.label}</span> ·{' '}
                    <Ago ts={r.startedAt ?? r.updatedAt} />
                  </span>
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </LaneSection>
  )
}

function Builds({ w, tasks }: { w: WorkstreamDetail; tasks: TaskDetail[] }) {
  const builds = useBuilds(w.projectId)
  const [feedback, setFeedback] = useState<BuildCard | null>(null)
  const ids = new Set(tasks.map((t) => t.id))
  const mine = (builds.data ?? []).filter(
    (b) => b.taskIds.some((t) => ids.has(t)) || b.tasks.some((t) => t.workstreamId === w.id),
  )
  if (!mine.length) return null
  return (
    <LaneSection id="ws-builds" title="Builds" count={mine.length}>
      <div className="grid gap-3">
        {mine.slice(0, 5).map((b) => (
          <BuildView key={b.buildId} card={b} onFeedback={() => setFeedback(b)} />
        ))}
      </div>
      {feedback ? <FeedbackSheet build={feedback} onClose={() => setFeedback(null)} /> : null}
    </LaneSection>
  )
}

// ---- task -----------------------------------------------------------------------------------

export function TaskPage() {
  const { taskId } = useParams({ strict: false }) as { taskId: string }
  return (
    <div className="app-list flex-1">
      <TaskView taskId={taskId} />
    </div>
  )
}

function TaskView({ taskId }: { taskId: string }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const nav = useNavigate()
  const ui = useAppUi()
  const t = useTask(taskId)
  const siblings = useTasks({ projectId: t.data?.projectId })
  const task = t.data
  if (t.isPending) return <SkeletonRows n={3} />
  if (!task) return <Empty title="Task not found">{errorText(t.error)}</Empty>
  const byId = new Map((siblings.data ?? []).map((x) => [x.id, x]))
  const set = async (patch: Parameters<typeof b.api.updateTask>[1], ok: string) => {
    try {
      await b.api.updateTask(task.id, patch)
      await qc.invalidateQueries({ queryKey: qk.task(task.id) })
      await qc.invalidateQueries({ queryKey: ['tasks'] })
      toast(ok)
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <div className="mx-auto max-w-[760px] pb-10">
      <header className="page-head">
        {task.workstreamId ? (
          <Link
            to="/w/$workstreamId"
            params={{ workstreamId: task.workstreamId }}
            className="vk-iconbtn"
            aria-label="Back to lane"
          >
            <ChevronLeft size={18} />
          </Link>
        ) : null}
        <h1 className="truncate">{task.title}</h1>
      </header>
      <div className="flex flex-wrap items-center gap-2 px-4 pb-3 text-[14px]">
        <Chip tone={task.status === 'blocked' ? 'wait' : task.status === 'done' ? 'ok' : undefined}>
          {task.status}
        </Chip>
        <Chip>priority {task.priority}</Chip>
        <Chip>by {task.provenance}</Chip>
        {task.agentHint ? <Chip>{task.agentHint}</Chip> : null}
        {task.labels.map((l) => (
          <Chip key={l}>{l}</Chip>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 px-4 pb-4">
        {task.status === 'proposed' ? (
          <Button
            size="sm"
            variant="primary"
            icon={<Check size={14} />}
            onClick={async () => {
              try {
                await b.api.approveTask(task.id)
                await qc.invalidateQueries({ queryKey: qk.task(task.id) })
                toast('Approved')
              } catch (e) {
                toast(errorText(e), 'error')
              }
            }}
          >
            Approve
          </Button>
        ) : null}
        {task.status === 'ready' && task.workstreamId ? (
          <Button
            size="sm"
            variant="primary"
            icon={<Play size={14} />}
            onClick={() =>
              ui.openStart({
                projectId: task.projectId,
                ...(task.workstreamId ? { workstreamId: task.workstreamId } : {}),
                taskId: task.id,
                prompt: task.title,
              })
            }
          >
            Start session
          </Button>
        ) : null}
        {task.status !== 'done' && task.status !== 'cancelled' && task.status !== 'proposed' ? (
          <Button size="sm" onClick={() => void set({ status: 'done' }, 'Marked done')}>
            Mark done
          </Button>
        ) : null}
        {task.status === 'blocked' ? (
          <Button size="sm" onClick={() => void set({ status: 'ready' }, 'Back to ready')}>
            Unblock
          </Button>
        ) : null}
        {task.status === 'done' || task.status === 'cancelled' ? (
          <Button size="sm" onClick={() => void set({ status: 'ready' }, 'Reopened')}>
            Reopen
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void set({ status: 'cancelled' }, 'Cancelled')}
          >
            Cancel task
          </Button>
        )}
        {task.runId ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<ArrowUpRight size={14} />}
            onClick={() => void nav({ to: '/now/$runId', params: { runId: task.runId as string } })}
          >
            Session
          </Button>
        ) : null}
      </div>
      {task.blockedReason ? (
        <p className="mx-4 mb-3 rounded-xl bg-status-wait-soft p-3 text-[14px]">
          Blocked: {task.blockedReason}
        </p>
      ) : null}
      {task.description ? (
        <section aria-label="Description" className="px-4 pb-3">
          <p className="m-0 text-[15px] leading-[21px] whitespace-pre-wrap">{task.description}</p>
        </section>
      ) : null}
      {task.acceptance.length ? (
        <section aria-labelledby="t-acc">
          <SectionTitle>
            <span id="t-acc">Done when</span>
          </SectionTitle>
          <ul className="m-0 grid gap-1 px-8 text-[15px]">
            {task.acceptance.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </section>
      ) : null}
      {task.deps.length ? (
        <section aria-labelledby="t-deps">
          <SectionTitle>
            <span id="t-deps">Depends on</span>
          </SectionTitle>
          {task.deps.map((d) => {
            const dep = byId.get(d)
            return (
              <Link key={d} to="/t/$taskId" params={{ taskId: d }} className="row">
                <span className="mt-0.5 flex-none">{TASK_ICON(dep?.status ?? 'ready')}</span>
                <span className="row__title">{dep?.title ?? d}</span>
              </Link>
            )
          })}
        </section>
      ) : null}
      {task.rationale ? (
        <section aria-labelledby="t-why">
          <SectionTitle>
            <span id="t-why">Why</span>
          </SectionTitle>
          <p className="m-0 px-4 text-[15px] text-text-2">{task.rationale}</p>
        </section>
      ) : null}
      <p className="m-0 px-4 pt-4 text-[14px] text-text-3">
        Created <Ago ts={task.createdAt} /> · updated <Ago ts={task.updatedAt} />
      </p>
    </div>
  )
}
